import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FiltroReporte, ValoresFiltro } from '@/services/api/reportes.api';

interface ReportFiltrosProps {
  filtros: FiltroReporte[];
  valores: ValoresFiltro;
  onChange: (clave: string, valor: string | string[] | boolean | undefined) => void;
}

/**
 * Controles de filtro generados desde el esquema del reporte.
 *
 * El tipo de control sale del operador y de si el filtro declara opciones, así
 * que un reporte nuevo aparece con sus filtros sin tocar este archivo.
 */
export function ReportFiltros({ filtros, valores, onChange }: ReportFiltrosProps) {
  if (filtros.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Este reporte no tiene filtros configurables.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {filtros.map((filtro) => {
        const valor = valores[filtro.clave];
        const id = `filtro-${filtro.clave}`;

        // Booleano → casilla. `undefined` es "sin filtrar", distinto de `false`.
        if (filtro.operador === 'booleano') {
          return (
            <label
              key={filtro.clave}
              htmlFor={id}
              className="flex items-center gap-2 text-sm cursor-pointer"
            >
              <input
                id={id}
                type="checkbox"
                className="h-4 w-4 rounded border-input accent-primary-600"
                checked={valor === true}
                onChange={(e) => onChange(filtro.clave, e.target.checked ? true : undefined)}
              />
              <span>{filtro.titulo}</span>
            </label>
          );
        }

        // Con opciones declaradas → selector. `en` admite varias.
        if (filtro.opciones?.length) {
          const multiple = filtro.operador === 'en';
          const seleccion = Array.isArray(valor) ? valor : valor ? [String(valor)] : [];

          return (
            <div key={filtro.clave} className="space-y-1">
              <Label htmlFor={id} className="text-xs">{filtro.titulo}</Label>
              <select
                id={id}
                multiple={multiple}
                // Alto suficiente para los enums del dominio (el mayor tiene 6
                // valores): con menos, la lista arranca scrolleada y la primera
                // opción aparece cortada.
                size={multiple ? Math.min(filtro.opciones.length, 6) : undefined}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={multiple ? seleccion : (seleccion[0] ?? '')}
                onChange={(e) => {
                  if (multiple) {
                    const elegidas = Array.from(e.target.selectedOptions, (o) => o.value);
                    onChange(filtro.clave, elegidas.length ? elegidas : undefined);
                  } else {
                    onChange(filtro.clave, e.target.value || undefined);
                  }
                }}
              >
                {/* A single option means the server pinned it for this role. */}
                {!multiple && filtro.opciones.length > 1 && <option value="">Todos</option>}
                {filtro.opciones.map((opcion) => (
                  <option key={opcion} value={opcion}>{opcion}</option>
                ))}
              </select>
            </div>
          );
        }

        // Rango de fechas → input de fecha; el resto, texto libre.
        const esFecha = filtro.operador === 'desde' || filtro.operador === 'hasta';

        return (
          <div key={filtro.clave} className="space-y-1">
            <Label htmlFor={id} className="text-xs">{filtro.titulo}</Label>
            <Input
              id={id}
              type={esFecha ? 'date' : 'text'}
              value={typeof valor === 'string' ? valor : ''}
              placeholder={esFecha ? undefined : 'Sin filtrar'}
              onChange={(e) => onChange(filtro.clave, e.target.value || undefined)}
            />
          </div>
        );
      })}
    </div>
  );
}
